import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowUpRight, Building2, ClipboardList, CreditCard, Database, FileText, Globe2, Mail, RefreshCw, Search, Settings2, ShieldCheck, Users } from 'lucide-react';
import { api, dateTime } from '../lib/api';
import {
  PROFILE_SERVICE_ENDPOINT,
  isActiveProfileSubscription,
  normaliseProfilesReferrals,
  normaliseProfilesService,
  profileReferralEndpoint,
  searchProfileCustomers,
  currentProfilesPlan,
  profilesPlanSummary,
  filterProfilesByPlan,
} from '../lib/profiles-admin-queries';

// All data and staff actions pass through existing authenticated, role-controlled
// SM-CUSTOMER-OPERATIONS APIs. Profiles D1 and legacy /api/admin are never queried.
const groups = [
  { title: 'Customer operations', links: [
    { label: 'Customers & Customer360', to: '/customers', icon: Users },
    { label: 'Organisations', to: '/organisations', icon: Building2 },
    { label: 'Customer data protection', to: '/data-protection', icon: Database },
  ] },
  { title: 'Moderation & support', links: [
    { label: 'Integrity reports', to: '/integrity', icon: AlertTriangle },
    { label: 'Back Office – all referrals', to: '/back-office', icon: ShieldCheck },
    { label: '18+ creator verification reviews', to: '/profiles/admin/creator-reviews', icon: ShieldCheck },
    { label: 'Cases and support', to: '/cases', icon: ClipboardList },
    { label: 'Customer communications', to: '/inbox', icon: Mail },
  ] },
  { title: 'Commercial & governance', links: [
    { label: 'Central billing', to: '/billing', icon: CreditCard },
    { label: 'Publications', to: '/communications/publications', icon: FileText },
    { label: 'CRM settings', to: '/settings', icon: Settings2 },
  ] },
];
const QUEUE_STATUSES = [
  ['open', 'Open'], ['in_progress', 'In progress'],
  ['waiting_customer', 'Waiting customer'], ['resolved', 'Resolved'],
  ['closed', 'Closed'], ['all', 'All'],
];
const friendly = value => String(value || 'Not recorded').replaceAll('_', ' ');
const customerLink = (id, section = 'services') => '/customers/' + encodeURIComponent(id) + '#' + section;

export default function ProfilesAdministration() {
  const [customers, setCustomers] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [serviceError, setServiceError] = useState('');
  const [queueError, setQueueError] = useState('');
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [queueStatus, setQueueStatus] = useState('open');
  const [selectedId, setSelectedId] = useState(null);
  const [claimingId, setClaimingId] = useState(null);
  const [notice, setNotice] = useState('');
  const requestSequence = useRef(0);

  const load = useCallback(async () => {
    const request = ++requestSequence.current;
    setLoading(true);
    const [service, queue] = await Promise.allSettled([
      api(PROFILE_SERVICE_ENDPOINT),
      api(profileReferralEndpoint(queueStatus)),
    ]);
    if (request !== requestSequence.current) return;
    if (service.status === 'fulfilled') {
      try {
        setCustomers(normaliseProfilesService(service.value));
        setServiceError('');
      } catch (error) {
        setCustomers([]);
        setServiceError(error.message);
      }
    } else {
      setCustomers([]);
      setServiceError(service.reason?.message || 'Profiles service records are unavailable.');
    }
    if (queue.status === 'fulfilled') {
      try {
        setReferrals(normaliseProfilesReferrals(queue.value));
        setQueueError('');
      } catch (error) {
        setReferrals([]);
        setQueueError(error.message);
      }
    } else {
      setReferrals([]);
      setQueueError(queue.reason?.message || 'Profiles Back Office referrals are unavailable.');
    }
    setLoading(false);
  }, [queueStatus]);

  useEffect(() => {
    void load();
    return () => { requestSequence.current += 1; };
  }, [load]);

  const planSummary = useMemo(() => profilesPlanSummary(customers), [customers]);
  const filteredCustomers = useMemo(() => filterProfilesByPlan(searchProfileCustomers(customers, query), selectedPlan), [customers, query, selectedPlan]);
  const selected = customers.find(row => String(row.customer_id) === String(selectedId)) || null;
  const activeCount = customers.filter(isActiveProfileSubscription).length;

  async function claim(referral) {
    if (!referral?.id || claimingId) return;
    setClaimingId(referral.id);
    setNotice('');
    try {
      await api('/api/back-office/referrals/' + encodeURIComponent(referral.id) + '/claim', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: '{}',
      });
      setNotice('Referral ' + (referral.reference || referral.id) + ' has been assigned to your staff account.');
      await load();
    } catch (error) {
      setNotice('Could not claim referral: ' + error.message);
    } finally {
      setClaimingId(null);
    }
  }

  return <main className="profiles-admin-workspace">
    <header className="profiles-admin-workspace-header">
      <div>
        <span className="profiles-admin-workspace-kicker">SOUSA MURRAY / CUSTOMER OPERATIONS</span>
        <h1 className="govuk-heading-xl">Profiles administration</h1>
        <p className="govuk-body">Central customer records and governed Profiles service operations. All information is read from the existing CRM; decisions are handled through authorised Back Office workflows.</p>
      </div>
      <button type="button" className="button secondary govuk-button govuk-button--secondary" onClick={() => void load()} disabled={loading}><RefreshCw size={16} />Refresh live records</button>
    </header>

    <section className="profiles-admin-metrics" aria-label="Profiles operations overview">
      <div><span>Service customers</span><strong>{serviceError ? '—' : customers.length}</strong><small>From central service authority</small></div>
      <div><span>Active subscriptions</span><strong>{serviceError ? '—' : activeCount}</strong><small>CRM subscription status</small></div>
      <div><span>Back Office referrals</span><strong>{queueError ? '—' : referrals.length}</strong><small>{QUEUE_STATUSES.find(row => row[0] === queueStatus)?.[1]} · Profiles</small></div>
    </section>

    {notice && <p className="profiles-admin-notice govuk-body" role="status">{notice}</p>}

    <section className="profiles-admin-panel" aria-labelledby="profiles-register-heading">
      <div className="profiles-admin-panel-heading">
        <div><span>CUSTOMER REGISTER</span><h2 className="govuk-heading-m" id="profiles-register-heading">Profiles customer records</h2><p className="govuk-body">Select a customer to review account and service details; use Customer360 for governed account actions.</p></div>
        <div className="profiles-admin-toolbar"><label className="profiles-admin-filter govuk-label"><span>Current plan</span><select className="govuk-select" value={selectedPlan} onChange={event => setSelectedPlan(event.target.value)}><option value="">All plans</option>{planSummary.map(plan => <option value={plan.name} key={plan.name}>{plan.name} ({plan.customers})</option>)}</select></label><label className="profiles-admin-search govuk-label"><Search size={16} aria-hidden="true" /><span className="sr-only">Search Profiles customers</span><input className="govuk-input" type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Name, number, email or plan" /></label></div>
      </div>
      {!serviceError && !loading && <div className="profiles-admin-plan-summary" aria-label="Current assigned plan distribution"><span>Existing service assignments only — not the approved new price table</span>{planSummary.length ? planSummary.map(plan => <button key={plan.name} type="button" onClick={() => setSelectedPlan(current => current === plan.name ? "" : plan.name)} aria-pressed={selectedPlan === plan.name}><strong>{plan.name}</strong><small>{plan.customers} customer{plan.customers === 1 ? "" : "s"} · {plan.active} active</small></button>) : <span>No plan assignments available.</span>}</div>}
      {serviceError ? <p className="profiles-admin-error govuk-body" role="alert">Customer records unavailable: {serviceError}</p> :
        loading ? <p className="profiles-admin-empty govuk-body">Loading Profiles customers…</p> :
        <div className="profiles-admin-table-scroll"><table className="profiles-admin-table govuk-table">
          <thead className="govuk-table__head"><tr className="govuk-table__row"><th className="govuk-table__header">Customer</th><th className="govuk-table__header">Customer number</th><th className="govuk-table__header">Plan</th><th className="govuk-table__header">Subscription</th><th className="govuk-table__header">Operations</th></tr></thead>
          <tbody className="govuk-table__body">{filteredCustomers.map(row => <tr className="govuk-table__row" key={row.customer_id}>
            <td className="govuk-table__cell"><strong>{row.display_name || 'Customer'}</strong><small>{row.verified_email || 'No verified email in service register'}</small></td>
            <td className="govuk-table__cell">{row.customer_number || '—'}</td>
            <td className="govuk-table__cell">{currentProfilesPlan(row)}</td>
            <td className="govuk-table__cell"><span className="profiles-admin-status">{friendly(row.subscription_status || row.service_account_status)}</span></td>
            <td className="govuk-table__cell"><button type="button" className="profiles-admin-action" onClick={() => setSelectedId(row.customer_id)} aria-label={'Inspect ' + (row.display_name || row.customer_number || row.customer_id)}>Inspect</button></td>
          </tr>)}</tbody>
        </table>{filteredCustomers.length === 0 && <p className="profiles-admin-empty govuk-body">No authorised customer records match the search.</p>}</div>}
      {selected && <aside className="profiles-admin-detail" aria-label="Selected customer operations">
        <div><span>CUSTOMER360</span><h3 className="govuk-heading-s">{selected.display_name || 'Customer'}</h3><p className="govuk-body">{selected.customer_number || selected.customer_id} · {selected.verified_email || 'Email not shown'}</p></div>
        <div className="profiles-admin-detail-actions">
          <Link to={"/profiles/admin/customers/" + encodeURIComponent(selected.customer_id)} className="button primary govuk-button">Manage Profiles in CRM <ArrowUpRight size={14} /></Link>
          <Link to={customerLink(selected.customer_id)} className="button secondary govuk-button govuk-button--secondary">Open Customer360</Link>
          <Link to={customerLink(selected.customer_id, 'back-office')} className="button secondary govuk-button govuk-button--secondary">Open Back Office</Link>
          <button type="button" className="button secondary govuk-button govuk-button--secondary" onClick={() => setSelectedId(null)}>Close</button>
        </div>
      </aside>}
    </section>

    <section className="profiles-admin-panel" aria-labelledby="profiles-verification-heading">
      <div className="profiles-admin-panel-heading">
        <div><span>BACK OFFICE</span><h2 className="govuk-heading-m" id="profiles-verification-heading">Profiles referrals and verification</h2><p className="govuk-body">Live, permission-controlled referrals. Review each case in the existing CRM decision workspace, including evidence, verification, correspondence and audit history.</p></div>
        <label className="profiles-admin-filter govuk-label"><span>Queue status</span><select className="govuk-select" value={queueStatus} onChange={event => setQueueStatus(event.target.value)}>{QUEUE_STATUSES.map(([value, name]) => <option value={value} key={value}>{name}</option>)}</select></label>
      </div>
      {queueError ? <p className="profiles-admin-error govuk-body" role="alert">Back Office unavailable or not authorised: {queueError}</p> :
        loading ? <p className="profiles-admin-empty govuk-body">Loading referrals…</p> :
        referrals.length ? <div className="profiles-admin-table-scroll"><table className="profiles-admin-table govuk-table">
          <thead className="govuk-table__head"><tr className="govuk-table__row"><th className="govuk-table__header">Reference</th><th className="govuk-table__header">Customer</th><th className="govuk-table__header">Work</th><th className="govuk-table__header">Status</th><th className="govuk-table__header">Created</th><th className="govuk-table__header">Actions</th></tr></thead>
          <tbody className="govuk-table__body">{referrals.map(row => <tr className="govuk-table__row" key={row.id}>
            <td className="govuk-table__cell"><strong>{row.reference || row.id}</strong></td><td className="govuk-table__cell">{row.customer_name || row.customer_number || 'Customer'}</td>
            <td className="govuk-table__cell"><strong>{row.title || friendly(row.action_type)}</strong><small>{friendly(row.category)}</small></td>
            <td className="govuk-table__cell"><span className="profiles-admin-status">{friendly(row.status)}</span></td>
            <td className="govuk-table__cell">{dateTime(row.created_at)}</td>
            <td className="govuk-table__cell"><div className="profiles-admin-row-actions"><Link to={'/profiles/admin/referrals/' + encodeURIComponent(row.id)} className="profiles-admin-action">Review</Link>
              {!['resolved', 'closed'].includes(row.status) && <button type="button" className="profiles-admin-action" onClick={() => void claim(row)} disabled={claimingId === row.id}>Claim</button>}</div></td>
          </tr>)}</tbody>
        </table></div> : <p className="profiles-admin-empty govuk-body">No Profiles referrals in this queue.</p>}
    </section>

    <section className="profiles-admin-panel" aria-labelledby="profiles-other-heading">
      <div className="profiles-admin-panel-heading"><div><span>CONNECTED OPERATIONS</span><h2 className="govuk-heading-m" id="profiles-other-heading">Existing CRM control centres</h2><p className="govuk-body">These are existing operational workspaces, not certification that legacy Profiles administrative features have been transferred.</p></div></div>
      <div className="profiles-admin-links">{groups.map(group => <div key={group.title}><h3 className="govuk-heading-s">{group.title}</h3>{group.links.map(({label, to, icon: Icon}) => <Link to={to} key={to}><Icon size={15} aria-hidden="true" />{label}<ArrowUpRight size={13} aria-hidden="true" /></Link>)}</div>)}</div>
    </section>
    <p className="profiles-admin-legacy-notice govuk-body"><Globe2 size={16} aria-hidden="true" />The legacy Profiles Admin Centre remains operational until every function has a tested CRM counterpart and its retirement is explicitly approved. New 18+ controls, Explore administration and live plan changes are not activated by this workspace.</p>
  </main>;
}
